// image-trail-simple
import { onMount, onDestroy, onView } from "@/modules/_";
import { Resize } from "@lib/subs";
import { prefersReducedMotion } from "@/utils/media";
import gsap from "@lib/gsap";

const MIN_WIDTH = 992;
const MOVE_DIVISOR = 15;
const TRAIL_LENGTH = 8;
const ROTATION_AMOUNT = 8;
const STOP_DURATION_MS = 350;

const ROUND = "10px";
const FALLBACK_W = 148;
const FALLBACK_H = 186;
const ICON_FALLBACK_W = 40;
const ICON_FALLBACK_H = 40;
const ICON_PROBABILITY = 0.66;

// Never center-center — keeps icons visually anchored to card edges
const POSITION_PRESETS: { x: [number, number]; y: [number, number] }[] = [
  { x: [-5, 25], y: [-5, 20] },
  { x: [35, 65], y: [-10, 15] },
  { x: [75, 105], y: [-5, 20] },
  { x: [-10, 15], y: [35, 65] },
  { x: [85, 110], y: [35, 65] },
  { x: [-5, 25], y: [80, 105] },
  { x: [35, 65], y: [85, 110] },
  { x: [75, 105], y: [80, 105] },
];

const ENTRY_DURATION = 0.6;
const EXIT_DURATION = 0.8;
const ENTRY_EASE = "back.out(1.7)";
const EXIT_EASE = "expo.out";

const REDUCED_MOTION_FADE = 0.3;
const OFFSCREEN_ROOT_MARGIN = "200px";

export default function (element: HTMLElement, _dataset: DOMStringMap) {
  let rm = prefersReducedMotion();

  const items = Array.from(
    element.querySelectorAll<HTMLElement>('[image-trail="item"]'),
  );
  const sources = items
    .map((item) => item.querySelector<HTMLImageElement>('[image-trail="img"]'))
    .filter((img): img is HTMLImageElement => !!img)
    .map((img) => img.currentSrc || img.src);

  if (sources.length === 0) {
    console.warn("[image-trail-simple] no [image-trail='item'] sources found");
    return;
  }

  const iconItems = Array.from(
    element.querySelectorAll<HTMLElement>('[image-trail="icon-item"]'),
  );
  const iconSources = iconItems
    .map((item) =>
      item.querySelector<HTMLImageElement>('[image-trail="icon-img"]'),
    )
    .filter((img): img is HTMLImageElement => !!img)
    .map((img) => img.currentSrc || img.src);

  // Measure before hiding the list; fall back if 0 (e.g. list already display:none)
  let trailW = FALLBACK_W;
  let trailH = FALLBACK_H;
  let iconW = ICON_FALLBACK_W;
  let iconH = ICON_FALLBACK_H;
  function remeasure() {
    const r = items[0]?.getBoundingClientRect();
    if (r && r.width > 0) trailW = r.width;
    if (r && r.height > 0) trailH = r.height;
    const ir = iconItems[0]?.getBoundingClientRect();
    if (ir && ir.width > 0) iconW = ir.width;
    if (ir && ir.height > 0) iconH = ir.height;
  }
  remeasure();
  const offResize = Resize.add(() => {
    remeasure();
    nodes.forEach((n) => {
      n.style.width = `${trailW}px`;
      n.style.height = `${trailH}px`;
      const icon = n.querySelector<HTMLElement>(".about_hero_trail_icon");
      if (icon) {
        icon.style.width = `${iconW}px`;
        icon.style.height = `${iconH}px`;
      }
    });
    handleViewportGate();
  });

  const list = element.querySelector<HTMLElement>('[image-trail="list"]');
  let listFallbackApplied = false;
  let ariaApplied = false;
  if (list) {
    if (!list.hasAttribute("aria-hidden")) {
      list.setAttribute("aria-hidden", "true");
      ariaApplied = true;
    }
    const cs = getComputedStyle(list);
    const isVisible =
      cs.display !== "none" &&
      cs.visibility !== "hidden" &&
      parseFloat(cs.opacity) > 0;
    if (isVisible) {
      Object.assign(list.style, {
        position: "absolute",
        pointerEvents: "none",
        opacity: "0",
        width: "0",
        height: "0",
        overflow: "visible",
      });
      listFallbackApplied = true;
    }
  }

  const iconList = element.querySelector<HTMLElement>(
    '[image-trail="icon-list"]',
  );
  let iconListFallbackApplied = false;
  let iconAriaApplied = false;
  if (iconList) {
    if (!iconList.hasAttribute("aria-hidden")) {
      iconList.setAttribute("aria-hidden", "true");
      iconAriaApplied = true;
    }
    const cs = getComputedStyle(iconList);
    const isVisible =
      cs.display !== "none" &&
      cs.visibility !== "hidden" &&
      parseFloat(cs.opacity) > 0;
    if (isVisible) {
      Object.assign(iconList.style, {
        position: "absolute",
        pointerEvents: "none",
        opacity: "0",
        width: "0",
        height: "0",
        overflow: "visible",
      });
      iconListFallbackApplied = true;
    }
  }

  const positionWasUnset =
    !element.style.position && getComputedStyle(element).position === "static";
  if (positionWasUnset) element.style.position = "relative";

  // Pool must be larger than TRAIL_LENGTH so lead and tail never alias the same node
  const poolSize = Math.max(sources.length, TRAIL_LENGTH + 2);
  const nodes: HTMLElement[] = [];
  for (let i = 0; i < poolSize; i++) {
    const wrapper = document.createElement("div");
    wrapper.className = "about_hero_trail_cell";
    Object.assign(wrapper.style, {
      position: "absolute",
      pointerEvents: "none",
      top: "0px",
      left: "0px",
      width: `${trailW}px`,
      height: `${trailH}px`,
      translate: "-50% -50%",
      display: "none",
      willChange: "transform, opacity",
    });
    const img = document.createElement("img");
    img.className = "about_hero_trail_img";
    img.alt = "";
    img.decoding = "async";
    img.loading = "eager";
    img.src = sources[i % sources.length];
    Object.assign(img.style, {
      display: "block",
      width: "100%",
      height: "100%",
      objectFit: "cover",
      borderRadius: ROUND,
    });
    wrapper.appendChild(img);

    if (iconSources.length > 0 && Math.random() < ICON_PROBABILITY) {
      const iconSrc =
        iconSources[Math.floor(Math.random() * iconSources.length)];
      const preset =
        POSITION_PRESETS[Math.floor(Math.random() * POSITION_PRESETS.length)];
      const xPct = preset.x[0] + Math.random() * (preset.x[1] - preset.x[0]);
      const yPct = preset.y[0] + Math.random() * (preset.y[1] - preset.y[0]);
      const icon = document.createElement("img");
      icon.className = "about_hero_trail_icon";
      icon.alt = "";
      icon.decoding = "async";
      icon.loading = "eager";
      icon.src = iconSrc;
      Object.assign(icon.style, {
        position: "absolute",
        left: `${xPct}%`,
        top: `${yPct}%`,
        width: `${iconW}px`,
        height: `${iconH}px`,
        objectFit: "contain",
        pointerEvents: "none",
      });
      wrapper.appendChild(icon);
      const iconRotation = rm
        ? 0
        : (Math.random() < 0.5 ? -1 : 1) * ROTATION_AMOUNT;
      gsap.set(icon, {
        xPercent: -50,
        yPercent: -50,
        transformOrigin: "50% 50%",
        rotation: iconRotation,
        scale: 0,
      });
    }

    element.appendChild(wrapper);
    nodes.push(wrapper);
  }

  let globalIndex = 0;
  let last = { x: 0, y: 0 };
  const timestamps = new Map<HTMLElement, number>();
  let isActive = false;
  let cleanupInterval: number | null = null;

  // Shuffle bag — avoids back-to-back repeats across deck boundaries
  let imgDeck: number[] = [];
  let lastImgIdx = -1;
  function refillDeck(n: number, last: number): number[] {
    const d = Array.from({ length: n }, (_, i) => i);
    for (let i = d.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [d[i], d[j]] = [d[j], d[i]];
    }
    // Swap tail if it would produce an immediate repeat at deck boundary
    if (n > 1 && d[d.length - 1] === last) {
      [d[d.length - 1], d[d.length - 2]] = [d[d.length - 2], d[d.length - 1]];
    }
    return d;
  }
  function nextImgSrc(): string {
    if (!imgDeck.length) imgDeck = refillDeck(sources.length, lastImgIdx);
    const idx = imgDeck.pop()!;
    lastImgIdx = idx;
    return sources[idx];
  }

  function activate(node: HTMLElement, x: number, y: number) {
    const direction = globalIndex % 2 === 0 ? -1 : 1;
    const rotation = rm ? 0 : ROTATION_AMOUNT * direction;

    Object.assign(node.style, {
      left: `${x}px`,
      top: `${y}px`,
      zIndex: String(globalIndex),
      display: "block",
    });

    timestamps.set(node, performance.now());

    const img = node.querySelector<HTMLImageElement>(".about_hero_trail_img");
    if (img) img.src = nextImgSrc();

    const icon = node.querySelector<HTMLElement>(".about_hero_trail_icon");

    gsap.killTweensOf(node);
    if (icon) gsap.killTweensOf(icon);
    if (rm) {
      gsap.fromTo(
        node,
        { autoAlpha: 0 },
        { autoAlpha: 1, duration: REDUCED_MOTION_FADE, overwrite: true },
      );
      if (icon) gsap.set(icon, { scale: 1 });
    } else {
      gsap.fromTo(
        node,
        { autoAlpha: 0, scale: 0.35, rotation: rotation * 3, y: 18 },
        {
          autoAlpha: 1,
          scale: 1,
          rotation,
          y: 0,
          duration: ENTRY_DURATION,
          ease: ENTRY_EASE,
          overwrite: true,
        },
      );
      if (icon) {
        gsap.to(icon, {
          scale: 1,
          duration: ENTRY_DURATION,
          ease: ENTRY_EASE,
          overwrite: true,
        });
      }
    }

    last = { x, y };
  }

  function fadeOut(node: HTMLElement) {
    const icon = node.querySelector<HTMLElement>(".about_hero_trail_icon");
    gsap.killTweensOf(node);
    if (icon) gsap.killTweensOf(icon);
    if (rm) {
      gsap.to(node, {
        autoAlpha: 0,
        duration: REDUCED_MOTION_FADE,
        overwrite: true,
        onComplete: () => gsap.set(node, { display: "none" }),
      });
      return;
    }
    if (icon) {
      gsap.to(icon, {
        scale: 0,
        duration: EXIT_DURATION,
        ease: EXIT_EASE,
        overwrite: true,
      });
    }
    const currentRotation = (gsap.getProperty(node, "rotation") as number) || 0;
    gsap.to(node, {
      autoAlpha: 0,
      scale: 0.2,
      rotation: currentRotation > 0 ? currentRotation + 22 : currentRotation - 22,
      y: -12,
      duration: EXIT_DURATION,
      ease: EXIT_EASE,
      overwrite: true,
      onComplete: () => gsap.set(node, { display: "none" }),
    });
  }

  function onMove(e: MouseEvent) {
    if (!isActive) return;
    const rect = element.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const dist = Math.hypot(x - last.x, y - last.y);
    if (dist <= window.innerWidth / MOVE_DIVISOR) return;

    const lead = nodes[globalIndex % nodes.length];
    const tailIdx =
      ((globalIndex - TRAIL_LENGTH) % nodes.length + nodes.length) %
      nodes.length;
    const tail = nodes[tailIdx];

    activate(lead, x, y);
    fadeOut(tail);
    globalIndex++;
  }

  function cleanupIdle() {
    const now = performance.now();
    for (const [node, t] of timestamps.entries()) {
      if (now - t > STOP_DURATION_MS) {
        fadeOut(node);
        timestamps.delete(node);
      }
    }
  }

  function start() {
    if (isActive || window.innerWidth < MIN_WIDTH) return;
    isActive = true;
    last = { x: 0, y: 0 };
    element.addEventListener("mousemove", onMove);
    cleanupInterval = window.setInterval(cleanupIdle, 100);
  }

  function stop() {
    if (!isActive) return;
    isActive = false;
    element.removeEventListener("mousemove", onMove);
    if (cleanupInterval !== null) {
      clearInterval(cleanupInterval);
      cleanupInterval = null;
    }
    nodes.forEach(fadeOut);
    timestamps.clear();
  }

  // onView only activates above MIN_WIDTH; resize handler re-checks the gate
  let inView = false;
  function handleViewportGate() {
    if (window.innerWidth < MIN_WIDTH) {
      stop();
    } else if (inView) {
      start();
    }
  }

  const rmMql = window.matchMedia("(prefers-reduced-motion: reduce)");
  function onRMChange(e: MediaQueryListEvent) {
    rm = e.matches;
  }
  rmMql.addEventListener("change", onRMChange);

  onMount(() => {
    onView(element, {
      rootMargin: OFFSCREEN_ROOT_MARGIN,
      autoStart: true,
      callback: ({ isIn }) => {
        inView = isIn;
        if (isIn) start();
        else stop();
      },
    });
  });

  onDestroy(() => {
    stop();
    offResize();
    rmMql.removeEventListener("change", onRMChange);

    nodes.forEach((n) => {
      gsap.killTweensOf(n);
      n.remove();
    });
    nodes.length = 0;
    timestamps.clear();

    if (positionWasUnset) element.style.position = "";
    if (list && listFallbackApplied) {
      Object.assign(list.style, {
        position: "",
        pointerEvents: "",
        opacity: "",
        width: "",
        height: "",
        overflow: "",
      });
    }
    if (list && ariaApplied) list.removeAttribute("aria-hidden");
    if (iconList && iconListFallbackApplied) {
      Object.assign(iconList.style, {
        position: "",
        pointerEvents: "",
        opacity: "",
        width: "",
        height: "",
        overflow: "",
      });
    }
    if (iconList && iconAriaApplied) iconList.removeAttribute("aria-hidden");
  });
}
